/**
 * DevTools deterrent for everyone except master admins.
 *
 *   1. Blocks the right-click menu and the DevTools / view-source shortcuts
 *      (F12, Ctrl+Shift+I/J/C, Ctrl+U — and the Cmd+Option equivalents on Mac).
 *   2. Runs a `debugger` loop: while DevTools is open, the page freezes on it.
 *   3. Blanks the page while DevTools looks open — detected from how long that
 *      `debugger` paused, or from the window being much wider/taller than the page.
 *
 * ⚠️ This is a DETERRENT, not data protection. DevTools can still be opened
 * from the browser menu, the debugger loop is defeated by "Deactivate
 * breakpoints", JavaScript can be switched off, and scrapers call the API
 * directly without any browser at all. What actually protects listing data is
 * server-side (field allowlists, page caps, per-account limits).
 *
 * Built bundles only (staging + production) — never the Vite dev server.
 */

const ENABLED = import.meta.env.PROD;
const TICK_MS = 1000;

// A `debugger` statement costs ~0ms unless DevTools is open, in which case it
// pauses until a human resumes it.
const PAUSE_MS = 100;

// DevTools docked to the side widens ONLY the horizontal gap between window
// and page; docked to the bottom, ONLY the vertical one. Browser zoom shrinks
// the page on BOTH axes at once, so requiring the other axis to stay normal
// keeps zoomed-in customers from being blanked. Browser toolbars alone are
// ~80–170px tall, hence the vertical allowance for the side-dock case.
const SIDE_GAP_MIN = 200;
const SIDE_GAP_MAX_VERTICAL = 200;
const BOTTOM_GAP_MIN = 300;
const BOTTOM_GAP_MAX_HORIZONTAL = 50;

const MESSAGE = "Developer tools are not available on this site. Close them to continue.";

let exempt = false;
let timer = null;
let overlay = null;

const isEditable = (el) =>
  !!el?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"])');

export const isBlockedShortcut = (e) => {
  if (e.key === "F12") return true;
  if (!(e.ctrlKey || e.metaKey)) return false;
  // Ctrl+Shift+I/J/C (Windows/Linux), Cmd+Option+I/J/C (Mac): DevTools, console, inspect.
  if ((e.shiftKey || e.altKey) && ["KeyI", "KeyJ", "KeyC"].includes(e.code)) return true;
  // View source. Left alone inside editors, where Ctrl+U means underline.
  return e.code === "KeyU" && !isEditable(e.target);
};

export const looksLikeDockedDevtools = ({ outerWidth, innerWidth, outerHeight, innerHeight }) => {
  const horizontal = outerWidth - innerWidth;
  const vertical = outerHeight - innerHeight;
  return (
    (horizontal > SIDE_GAP_MIN && vertical < SIDE_GAP_MAX_VERTICAL) ||
    (vertical > BOTTOM_GAP_MIN && horizontal < BOTTOM_GAP_MAX_HORIZONTAL)
  );
};

const setBlank = (on) => {
  if (on && !overlay) {
    overlay = document.createElement("div");
    overlay.setAttribute("role", "alert");
    overlay.textContent = MESSAGE;
    // Built outside React and styled inline on purpose: it must work even if
    // the app's stylesheets haven't loaded.
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;" +
      "justify-content:center;padding:24px;background:#fff;color:#111;" +
      "font:16px/1.5 system-ui,sans-serif;text-align:center";
    document.documentElement.appendChild(overlay);
  } else if (!on && overlay) {
    overlay.remove();
    overlay = null;
  }
};

const tick = () => {
  if (exempt) return;
  const before = performance.now();
  // eslint-disable-next-line no-debugger -- the freeze is the point
  debugger;
  const paused = performance.now() - before > PAUSE_MS;
  // Size check skipped where it can only misfire: embedded in another page
  // (e.g. the Shopify admin) the outer size is the parent window's, and on
  // touch devices the on-screen keyboard can shrink the page height — there is
  // no docked DevTools on a phone anyway.
  const docked =
    window.top === window &&
    !window.matchMedia("(pointer: coarse)").matches &&
    looksLikeDockedDevtools(window);
  setBlank(paused || docked);
};

const onKeyDown = (e) => {
  if (exempt || !isBlockedShortcut(e)) return;
  e.preventDefault();
  e.stopPropagation();
};

// Right-click stays available in form fields so paste keeps working; opening
// DevTools from that menu still trips the loop and the blanking.
const onContextMenu = (e) => {
  if (!exempt && !isEditable(e.target)) e.preventDefault();
};

const cachedUserIsMasterAdmin = () => {
  try {
    return !!JSON.parse(localStorage.getItem("user"))?.is_master_admin;
  } catch {
    return false;
  }
};

export const startDevtoolsGuard = () => {
  if (!ENABLED || timer) return;
  // The signed-in user is cached in localStorage, so master admins are exempt
  // from the very first tick; DevtoolsGuard keeps this in step afterwards.
  exempt = cachedUserIsMasterAdmin();
  window.addEventListener("keydown", onKeyDown, true);
  window.addEventListener("contextmenu", onContextMenu, true);
  timer = setInterval(tick, TICK_MS);
};

export const setDevtoolsGuardExempt = (value) => {
  exempt = !!value;
  if (exempt) setBlank(false);
};
